import type { AdSize, SizeMapping } from "@sphtech/web2-ads";

import type { PageAdsContext } from "./pageAds";

/** One `<AdSlot>` mounted into a legacy `data-layout-position` placeholder. */
export interface AdPlacementAssignment {
  /** Stable React key / portal identity. */
  key: string;
  container: Element;
  placement: string;
  /** SDK slot instance key for repeated placements (e.g. `lb1-2`). */
  slotName?: string;
  targeting?: Record<string, string | string[]>;
  /** Explicit size override (else the SDK uses the registry slot sizes). */
  sizes?: AdSize[];
  sizeMapping?: SizeMapping;
  /** Sidebar slots keep the legacy sticky-rail container hook. */
  sticky?: boolean;
  /** Legacy MAS/self-collapsing creatives close their holder via postMessage. */
  closeOnMessage?: number[];
  /** Positive article/page index marks SDK AdSlot instances for runtime handoff. */
  infiniteScrollIndex?: number;
}

/**
 * AI-summary `lb2` sizes with the `1x1` collapse size stripped — parity with
 * legacy `open_ai_summary()` (`clonead.size.replace(',1x1','')`), so the
 * summary lightbox always serves a real banner and never a 1x1 blank. Mirrors
 * the registry `lb2` article breakpoints minus `1x1`.
 */
const AI_SUMMARY_SIZE_MAPPING: SizeMapping = [
  { viewport: [1024, 0], sizes: [[728, 90]] },
  { viewport: [600, 0], sizes: [[728, 90]] },
  {
    viewport: [0, 0],
    sizes: [
      [320, 50],
      [320, 100],
      [300, 250],
    ],
  },
];
const AI_SUMMARY_SIZES: AdSize[] = [
  [728, 90],
  [320, 50],
  [320, 100],
  [300, 250],
];

export interface ScanState {
  claimed: WeakSet<Element>;
  /** Per-placement instance counters, for repeat slotName suffixes. */
  counters: Record<string, number>;
}

type GrapeshotTargeting = {
  channels?: string[];
};

type GrapeshotResolver =
  | string[]
  | GrapeshotTargeting
  | ((container: Element) => string[] | GrapeshotTargeting | undefined);

export function createScanState(): ScanState {
  return { claimed: new WeakSet(), counters: {} };
}

export function releaseAdPlacementClaim(state: ScanState, container: Element) {
  state.claimed.delete(container);
}

function layoutPositionSelector(position: string): string {
  return `[data-layout-position="${position}"]`;
}

function queryAllWithRoot(root: ParentNode, selector: string): Element[] {
  const rootMatch =
    root instanceof Element && root.matches(selector) ? [root] : [];
  return [...rootMatch, ...Array.from(root.querySelectorAll(selector))];
}

function queryFirstWithRoot(
  root: ParentNode,
  selector: string,
): Element | null {
  if (root instanceof Element && root.matches(selector)) {
    return root;
  }
  return root.querySelector(selector);
}

/**
 * Viewport width (px) below which sidebar placeholders resolve to the
 * `sidebarmobile` variant instead of `sidebardesktop` — parity with the legacy
 * gam.js `get_sidebar_query()` / `screen_width()` breakpoint.
 */
const SIDEBAR_VARIANT_BREAKPOINT_PX = 1000;

/**
 * Sidebar placeholder selector — parity with gam.js `get_sidebar_query()`:
 * the shared `sidebar` position plus the viewport-specific variant.
 */
function sidebarSelector(): string {
  const variant =
    typeof window !== "undefined" &&
    window.innerWidth < SIDEBAR_VARIANT_BREAKPOINT_PX
      ? "sidebarmobile"
      : "sidebardesktop";
  return `${layoutPositionSelector("sidebar")},${layoutPositionSelector(
    variant,
  )}`;
}

function infiniteScrollIndexForScopedRoot(
  root: ParentNode,
  index: number,
): number | undefined {
  return root === document || index <= 0 ? undefined : index;
}

function closestArticleForRoot(root: ParentNode): Element | null {
  return root instanceof Element ? root.closest("article") : null;
}

function documentArticleIndex(article: Element): number | undefined {
  const articles: Element[] = Array.from(document.querySelectorAll("article"));
  const index = articles.indexOf(article);
  return index < 0 ? undefined : index + 1;
}

function infiniteScrollIndexForArticle(
  article: Element | null,
  root: ParentNode,
  scopedIndex: number,
): number | undefined {
  // Prefer the article's position in document order so the first appended
  // article (page 2) is tagged 2 rather than 1, in line with the page_number
  // semantics; page 1 is intentionally not handed off.
  const documentIndex = article ? documentArticleIndex(article) : undefined;
  if (documentIndex !== undefined) {
    return documentIndex <= 1 ? undefined : documentIndex;
  }
  return infiniteScrollIndexForScopedRoot(root, scopedIndex);
}

function readArticleData(container: Element): {
  articleId?: string;
  contentCategory?: string;
  articleKeyword?: string;
} {
  const article = container.closest("article");
  const dataTag = article?.querySelector("[data-analytics-article]");
  if (!dataTag?.textContent) return {};
  try {
    const parsed = JSON.parse(dataTag.textContent) as {
      articleId?: string;
      content_category?: string;
      article_keyword?: string;
    };
    return {
      articleId: parsed.articleId,
      contentCategory: parsed.content_category,
      articleKeyword: parsed.article_keyword,
    };
  } catch {
    return {};
  }
}

function articleSlotTargeting({
  articleId,
  articleKeyword,
}: ReturnType<typeof readArticleData>): Record<string, string> {
  if (!articleId) return {};
  const tag = (articleKeyword ?? "")
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
    .join(",");
  // Untagged articles still send `tag` (empty) so the first article's
  // page-level tags don't apply to their slots.
  return { articleid: articleId, tag };
}

function isVisibleAdPlaceholder(holder: HTMLElement): boolean {
  for (
    let current: HTMLElement | null = holder;
    current;
    current = current.parentElement
  ) {
    const style = getComputedStyle(current);
    if (style.display === "none" || style.visibility !== "visible") {
      return false;
    }
  }

  return holder.getClientRects().length > 0 || Boolean(holder.offsetParent);
}

/**
 * Scan the document for legacy ad placeholders and assign SDK placements to
 * any not yet claimed. Safe to call repeatedly (initial mount + DOM mutations
 * from autoload/infinite scroll); already-claimed containers are skipped and
 * repeat instances get suffixed slot names.
 *
 * The position → placement mapping replicates the legacy gam.js
 * `insert_ad()` / `watch_autoload()` engine:
 * - `leaderboard`                → lb1 (ContentHub: first `midcontent` when no
 *                                  leaderboard band exists)
 * - `midcontent`                 → first unclaimed listing block gets lb2; later
 *                                  blocks get lb1 clones except internal search
 *                                  and `techshows`, which keep lb2 clones; lb1
 *                                  clones between infinite-scroll articles
 * - `sidebar[i]`                 → imu1..imu4 on listing-type pages;
 *                                  imu2/imu3 per article on article pages
 * - `midarticle[0..2]`           → imu1 / midarticlespecial / midarticlespecial2
 *                                  per article body
 */
export function scanAdPlacements(
  context: PageAdsContext,
  state: ScanState,
  root: ParentNode = document,
  /**
   * Resolved Grapeshot data (from `readLegacyGrapeshotTargeting`), or a
   * per-placeholder resolver, applied as targeting on slots the SDK hands off to
   * infinite scroll. Page-1 placements never carry an `infiniteScrollIndex`, so
   * only the appended (autoload) ads receive it.
   */
  gsChannels?: GrapeshotResolver,
): AdPlacementAssignment[] {
  const out: AdPlacementAssignment[] = [];
  const { slots, contentKind, adSection } = context;
  const isFullDocumentScan = root === document;
  const isListingLike = ["index", "listing", "internal search"].includes(
    contentKind,
  );

  const claim = (
    container: Element | null | undefined,
    placement: string,
    targeting?: Record<string, string | string[]>,
    sticky?: boolean,
    closeOnMessage?: number[],
    infiniteScrollIndex?: number,
  ) => {
    if (!container || state.claimed.has(container)) return;
    if (!slots.includes(placement)) return;
    // Clear any stale SDK-injected inline styles left by a previous MAS
    // collapse or rail-hide so a re-claimed placeholder (e.g. after an SPA
    // route transition creates a fresh scan state) renders visible again.
    if (container instanceof HTMLElement) {
      container.style.display = "";
      container.style.height = "";
      container.style.overflow = "";
      container.style.padding = "";
      delete container.dataset.sdkMasClosing;
    }
    state.claimed.add(container);
    const n = state.counters[placement] ?? 0;
    state.counters[placement] = n + 1;
    // Infinite-scroll (autoload) slots carry Grapeshot channels for the article
    // they belong to, so appended article/ad pages target like their own loads.
    const mergedTargeting: Record<string, string | string[]> = { ...targeting };
    const resolvedGs =
      typeof gsChannels === "function" ? gsChannels(container) : gsChannels;
    const resolvedGsChannels = Array.isArray(resolvedGs)
      ? resolvedGs
      : resolvedGs?.channels;
    if (infiniteScrollIndex) {
      if (resolvedGsChannels?.length) {
        mergedTargeting.gs_channels = resolvedGsChannels;
      }
    }
    out.push({
      key: `${placement}-${n}`,
      container,
      placement,
      slotName: n > 0 ? `${placement}-${n + 1}` : undefined,
      targeting: mergedTargeting,
      sticky,
      closeOnMessage,
      infiniteScrollIndex,
    });
  };

  const leaderboard = queryFirstWithRoot(
    root,
    layoutPositionSelector("leaderboard"),
  );
  const midcontents = queryAllWithRoot(
    root,
    layoutPositionSelector("midcontent"),
  );
  const sidebars = queryAllWithRoot(root, sidebarSelector());

  // lb1 — the top leaderboard band; ContentHub (`topics`) has no band and
  // serves lb1 into its first mid-content block instead.
  if (leaderboard) {
    claim(leaderboard, "lb1");
  } else if (isFullDocumentScan && adSection === "topics") {
    claim(midcontents[0], "lb1");
  }

  if (isListingLike) {
    // First unclaimed mid-content block serves lb2 (legacy `insert_ad`); the
    // rest depend on the content kind (legacy listing fill + autoload flows):
    // - homepage (`index`): only the first block carries an ad
    // - listing: lb1 clones with `page_number` targeting (`techshows`: lb2)
    // - internal search: lb2 clones
    const lb2Placed = () => (state.counters["lb2"] ?? 0) > 0;
    midcontents.forEach((el, i) => {
      if (!lb2Placed()) {
        claim(el, "lb2");
        return;
      }
      if (contentKind === "index") return;
      const placement =
        contentKind === "internal search" || adSection === "techshows"
          ? "lb2"
          : "lb1";
      claim(
        el,
        placement,
        placement === "lb1"
          ? {
              page_number: isFullDocumentScan
                ? `${i + 1}`
                : `${(state.counters["lb1"] ?? 0) + 1}`,
            }
          : undefined,
      );
    });

    // Sidebar rails: imu1–imu4 down the page (internal search only serves
    // imu1/imu2); autoloaded rails beyond those keep serving imu1 clones
    // (parity with the legacy listing/search `watch_autoload` flows — the
    // homepage has no autoload, so `index` claims no overflow rails).
    const railPlacements =
      contentKind === "internal search"
        ? ["imu1", "imu2"]
        : ["imu1", "imu2", "imu3", "imu4"];
    // For scoped (infinite-scroll) rescans, tag sidebar slots with the
    // explore-more batch index so the SDK runtime can trigger
    // renderInfiniteScrollAds for each appended page of results.
    const sidebarBatchIndex = isFullDocumentScan
      ? undefined
      : (state.counters["imu1"] ?? 0) + 1;
    sidebars.forEach((sidebar, i) => {
      const placement =
        railPlacements[i] ?? (contentKind === "index" ? "" : "imu1");
      if (placement) {
        claim(
          sidebar,
          placement,
          undefined,
          true,
          undefined,
          infiniteScrollIndexForScopedRoot(root, sidebarBatchIndex ?? 0),
        );
      }
    });
    return out;
  }

  if (contentKind !== "article") return out;

  const scopedArticle = closestArticleForRoot(root);

  // Article pages — inline and sidebar slots are grouped per <article> so
  // infinite-scroll articles each get their own imu1/mid-article set, with
  // per-article `articleid` targeting (parity with the legacy autoload flow).
  const articles = queryAllWithRoot(root, "article");
  const articlesToScan =
    articles.length > 0 ? articles : scopedArticle ? [scopedArticle] : [];
  articlesToScan.forEach((article, articleIndex) => {
    const infiniteScrollIndex = infiniteScrollIndexForArticle(
      article,
      root,
      articleIndex + 1,
    );
    const midarticles = queryAllWithRoot(
      article,
      layoutPositionSelector("midarticle"),
    );
    if (midarticles.length === 0) return;

    const articleData = readArticleData(article);
    const targeting = articleSlotTargeting(articleData);

    // Sponsored (brand-studio) article bodies never carry inline ads.
    if (articleData.contentCategory === "ads") return;

    claim(
      midarticles[0],
      "imu1",
      targeting,
      undefined,
      [1],
      infiniteScrollIndex,
    );
    claim(
      midarticles[1],
      "midarticlespecial",
      targeting,
      undefined,
      [1],
      infiniteScrollIndex,
    );
    claim(
      midarticles[2],
      "midarticlespecial2",
      targeting,
      undefined,
      [1],
      infiniteScrollIndex,
    );
  });

  // Sidebars alternate imu2/imu3 down the page (first article's rail, then
  // each infinite-scroll article's rail).
  sidebars.forEach((sidebar) => {
    const sidebarTargeting = articleSlotTargeting(readArticleData(sidebar));
    const sidebarIndex =
      (state.counters["imu2"] ?? 0) + (state.counters["imu3"] ?? 0);
    const article = sidebar.closest("article");
    const infiniteScrollIndex = infiniteScrollIndexForArticle(
      article,
      root,
      articles.indexOf(article as Element) + 1,
    );
    claim(
      sidebar,
      sidebarIndex % 2 === 0 ? "imu2" : "imu3",
      sidebarTargeting,
      true,
      undefined,
      infiniteScrollIndex,
    );
  });

  // Mid-content blocks between infinite-scroll articles serve lb1 clones with
  // slot-level page_number (legacy autoload sets data-page-number = 2, 3, …).
  midcontents.forEach((el, i) => {
    const midcontentTargeting = articleSlotTargeting(readArticleData(el));
    const pageNumber = isFullDocumentScan
      ? i + 2
      : (state.counters["lb1"] ?? 0) + 1;
    claim(
      el,
      "lb1",
      {
        ...midcontentTargeting,
        page_number: `${pageNumber}`,
      },
      undefined,
      undefined,
      isFullDocumentScan
        ? undefined
        : infiniteScrollIndexForArticle(
            el.closest("article"),
            root,
            pageNumber,
          ),
    );
  });

  return out;
}

/**
 * Gallery-lightbox slots — parity with gam.js `handle_lightbox_gallery()`:
 * when the active lightbox slide is an inserted `gallerylightboxitem`
 * placeholder, serve a 300x250, cycling imu1 → midarticlespecial →
 * midarticlespecial2 by slide bucket (legacy `-gallery` slots).
 * Driven by the `lightbox-open(-image)=N` / `lightbox-change=N` postMessages
 * the gallery components already send.
 */
const GALLERY_SLIDE_PLACEMENTS = [
  "imu1",
  "midarticlespecial",
  "midarticlespecial2",
];
const GALLERY_SLIDES_PER_AD = 3;
const GALLERY_AD_SIZES: AdSize[] = [[300, 250]];
// Same slide lookup as legacy gam.js — coupled to the design-system
// CSS-module class prefixes, which is the existing production contract.
const GALLERY_SLIDE_SELECTOR =
  "div[class^=_lightbox_] div[class^=_embeddedgalleryContainer_] > div";

export function scanGalleryLightboxPlacements(
  context: PageAdsContext,
  state: ScanState,
  slideIndex: number,
): AdPlacementAssignment[] {
  const placement =
    GALLERY_SLIDE_PLACEMENTS[Math.floor(slideIndex / GALLERY_SLIDES_PER_AD)];
  if (!placement || !context.slots.includes(placement)) return [];

  const slides = document.querySelectorAll(GALLERY_SLIDE_SELECTOR);
  const holder = slides[slideIndex]?.querySelector(
    layoutPositionSelector("gallerylightboxitem"),
  );
  if (!holder || state.claimed.has(holder)) return [];

  state.claimed.add(holder);
  // Always namespace the gallery slot with a "-gallery" suffix (never a bare
  // placement name): the same placements also serve in the article body, so the
  // gallery instance needs its own slot identity (legacy `dfp-ad-<unit>-gallery`).
  // Repeat gallery ads then get an extra numeric suffix on top of that below.
  const counterKey = `${placement}-gallery`;
  const n = state.counters[counterKey] ?? 0;
  state.counters[counterKey] = n + 1;
  return [
    {
      key: `${counterKey}-${n}`,
      container: holder,
      placement,
      slotName: n > 0 ? `${counterKey}-${n + 1}` : counterKey,
      sizes: GALLERY_AD_SIZES,
    },
  ];
}

/**
 * AI-summary lightbox slots — mounted on demand when the design system pushes
 * the legacy `SPHMGAM.cmd` `"aisummary"` command. Serves lb2 with the
 * summary-page slot keyword (parity with gam.js `open_ai_summary()`).
 */
export function scanAiSummaryPlacements(
  context: PageAdsContext,
  state: ScanState,
): AdPlacementAssignment[] {
  const out: AdPlacementAssignment[] = [];
  if (!context.slots.includes("lb2")) return out;

  const targets = Array.from(
    document.querySelectorAll(layoutPositionSelector("aisummary")),
  );
  targets.forEach((el) => {
    if (state.claimed.has(el)) return;
    const holder = el as HTMLElement;
    if (!isVisibleAdPlaceholder(holder)) return;
    state.claimed.add(el);
    const n = state.counters["lb2-summary"] ?? 0;
    state.counters["lb2-summary"] = n + 1;
    out.push({
      key: `lb2-summary-${n}`,
      container: el,
      placement: "lb2",
      slotName: `lb2-summary-${n + 1}`,
      // Strip the 1x1 collapse size so the summary always serves a real banner
      // (legacy open_ai_summary parity).
      sizes: AI_SUMMARY_SIZES,
      sizeMapping: AI_SUMMARY_SIZE_MAPPING,
      targeting: {
        page: "summary",
        ...(context.articleId ? { articleid: context.articleId } : {}),
      },
    });
  });
  return out;
}
